import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { isEqual } from "@common/helpers/comparison";
import { EffectiveSplitDescriptionParams } from "@common/helpers/split-aware-description.helper";
import { TransactionFilters } from "@common/schemas/transaction.schema";
import { useNormalizedDescriptions } from "@hooks/useNormalizedDescriptions";
import { PaginatedTransactionsDataContext } from "../../../transactions/PaginatedTransactionsTable.context";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
} from "../../../settings/Rules/TransactionRuleForm/constants";
import { ActionRowDefinition, ActionRowRuleMatch } from "./types";
import {
    buildBaseActionRow,
    buildDefaultConditions,
    buildFilterScopedActionRow,
    buildManualActionRow,
    buildMatchingScopeConditions,
    buildScopeConditions,
    extractRowActionKey,
    stringifyActionValue,
} from "./seedActionRow";
import {
    buildRowIdToOwnedTxIds,
    reconcileActionRowsWithNewValues,
    removeActionRow,
} from "./actionRowsState";

const EMPTY_MERGED_TO_CANONICAL_MAP: Record<string, string> = {};

interface ActionsContextValue {
    rows: ActionRowDefinition[];
    ruleGroupId: string;
    setRuleGroupId: (id: string) => void;
    valueToAssign: BulkUpdateParams;
    filters: TransactionFiltersDto;
    similarToFallbackLabel?: string;
    /**
     * Default conditions seeded into a row only after the user adds the first
     * condition. Empty rows are update-only and do not create/apply rules.
     */
    defaultConditions: TransactionRuleConditionFormSchema[];
    /**
     * When `false` the drawer is opened with no filter context that yields
     * seedable rule conditions AND no row carries any user-added condition,
     * so rules can never be created. UI surfaces that depend on conditions
     * (the Condition cell, the Create rule toggle) hide themselves to avoid
     * showing a permanently empty column.
     */
    isRuleAuthoringAvailable: boolean;
    /**
     * Conditions that stay hidden in the row UI but are needed for backend rule
     * execution once a row has user-visible conditions.
     */
    scopeConditions: TransactionRuleConditionFormSchema[];
    /**
     * Conditions used only to resolve which transactions match an action row.
     * This includes transient drawer filters so preview/apply/delete behavior
     * stays inside the same filtered set the user is reviewing.
     */
    matchingScopeConditions: TransactionRuleConditionFormSchema[];
    /**
     * Total number of transactions in scope of the drawer (i.e. matching the
     * filters). Used to derive the base row's bucket count without
     * enumerating every transaction id.
     */
    totalTransactions: number;
    /**
     * Derived per-row transaction ownership. Map of `row.id` → list of
     * transaction ids currently assigned to that non-base row, computed from
     * `newValuesMap` keyed by stringified action value. The base row is
     * intentionally absent: its bucket is everything else, derived as
     * `totalTransactions - Σ(non-base owned ids)`.
     */
    rowIdToOwnedTxIds: Map<string, number[]>;
    updateRow(
        id: string,
        /**
         * `null` clears the field, `undefined` leaves it unchanged, any other
         * value assigns it. Keeps the call sites for duplicate detection etc.
         * simple without needing a separate "clearError" action.
         */
        patch: {
            conditions?: TransactionRuleConditionFormSchema[];
            action?: TransactionRuleActionFormSchema;
            createRule?: boolean;
            ruleMatch?: ActionRowRuleMatch | null;
            defaultConditionIds?: string[];
        },
    ): void;
    upsertFilterScopedRow: (params: {
        value: BulkUpdateParams["value"];
        filters: TransactionFilters;
        transactionCount: number;
    }) => void;
    appendEmptyRow: () => void;
    removeRow: (id: string) => void;
    reorderRows: (fromIndex: number, toIndex: number) => void;
}

const ActionsContext = createContext<ActionsContextValue | null>(null);

function normalizeConditionId(condition: TransactionRuleConditionFormSchema) {
    return { ...condition, id: "" };
}

function areConditionsEqualIgnoringIds(
    left: TransactionRuleConditionFormSchema[],
    right: TransactionRuleConditionFormSchema[],
): boolean {
    return isEqual(
        left.map(normalizeConditionId),
        right.map(normalizeConditionId),
    );
}

interface SyncBaseRowWithDefaultsParams {
    row: ActionRowDefinition;
    valueToAssign: BulkUpdateParams;
    nextBaseActionKey: string;
    nextBaseRow: ActionRowDefinition;
}

function syncBaseRowWithDefaults({
    row,
    valueToAssign,
    nextBaseActionKey,
    nextBaseRow,
}: SyncBaseRowWithDefaultsParams): ActionRowDefinition {
    if (!row.isBaseAction) {
        return row;
    }

    const currentBaseActionKey = extractRowActionKey(valueToAssign.update, row);
    const shouldUpdateAction =
        currentBaseActionKey !== nextBaseActionKey ||
        row.actionType !== nextBaseRow.actionType;
    const currentDefaultConditions = row.conditions.filter((condition) =>
        row.defaultConditionIds.includes(condition.id),
    );
    const shouldUpdateDefaultConditions = !areConditionsEqualIgnoringIds(
        currentDefaultConditions,
        nextBaseRow.conditions,
    );

    if (!shouldUpdateAction && !shouldUpdateDefaultConditions) {
        return row;
    }

    const customConditions = row.conditions.filter(
        (condition) => !row.defaultConditionIds.includes(condition.id),
    );
    return {
        ...row,
        id: shouldUpdateAction ? nextBaseRow.id : row.id,
        action: shouldUpdateAction ? nextBaseRow.action : row.action,
        actionType: shouldUpdateAction
            ? nextBaseRow.actionType
            : row.actionType,
        ruleMatch: shouldUpdateAction ? undefined : row.ruleMatch,
        conditions: shouldUpdateDefaultConditions
            ? [...nextBaseRow.conditions, ...customConditions]
            : row.conditions,
        defaultConditionIds: shouldUpdateDefaultConditions
            ? nextBaseRow.defaultConditionIds
            : row.defaultConditionIds,
    };
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActionsContext() {
    const ctx = useContext(ActionsContext);
    if (!ctx) {
        throw new Error(
            "useActionsContext must be used within an ActionsProvider",
        );
    }
    return ctx;
}

interface ProviderProps {
    valueToAssign: BulkUpdateParams;
    filters: TransactionFiltersDto;
    sourceSplitParams: EffectiveSplitDescriptionParams;
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
    defaultRuleGroupId: string;
    totalTransactions: number;
    similarToFallbackLabel?: string;
    children: React.ReactNode;
}

export const ActionsProvider: React.FC<ProviderProps> = ({
    valueToAssign,
    filters,
    sourceSplitParams,
    newValuesMap,
    defaultRuleGroupId,
    totalTransactions,
    similarToFallbackLabel,
    children,
}) => {
    /**
     * Default/scope conditions seeded once from `filters`. Defaults are cloned
     * into the base row immediately, and into manual/override rows only when
     * the first custom condition is added. Scope conditions are merged only for
     * rows that already have conditions.
     *
     * The canonical-id map is needed so a `filters.normalizedDescriptionId`
     * pointing at a child of a merged group resolves to the same canonical
     * parent the drawer's transaction list uses; otherwise the rule API
     * scopes SIMILAR_TO to a single description and returns fewer matches
     * than the drawer shows.
     */
    const { data: normalizedDescriptionsData } = useNormalizedDescriptions();
    const mergedToCanonicalMap =
        normalizedDescriptionsData?.mergedToCanonicalMap ??
        EMPTY_MERGED_TO_CANONICAL_MAP;

    // Union of the entity ids of the transactions actually shown in the drawer
    // preview. The rule's ENTITY scope is derived from this rather than the raw
    // `filters.entityIds`, so a saved rule always covers what the user saw even
    // when the preview query widened/sanitized its own entity filter away from
    // the single source-transaction entity (PRO-11168).
    const { transactions } = useContext(PaginatedTransactionsDataContext);
    const previewedEntityIds = useMemo(
        () => [
            ...new Set(
                (transactions ?? []).map(
                    (transaction) => transaction.entity.id,
                ),
            ),
        ],
        [transactions],
    );

    const defaultConditions = useMemo(
        () =>
            buildDefaultConditions(
                filters,
                mergedToCanonicalMap,
                sourceSplitParams,
            ),
        [filters, mergedToCanonicalMap, sourceSplitParams],
    );
    const scopeConditions = useMemo(
        () => buildScopeConditions(filters, previewedEntityIds),
        [filters, previewedEntityIds],
    );
    const matchingScopeConditions = useMemo(
        () => buildMatchingScopeConditions(filters, previewedEntityIds),
        [filters, previewedEntityIds],
    );

    const [rows, setRows] = useState<ActionRowDefinition[]>(() => [
        buildBaseActionRow(valueToAssign, defaultConditions),
    ]);
    const [ruleGroupId, setRuleGroupId] = useState(defaultRuleGroupId);

    useEffect(() => {
        const nextBaseActionKey = stringifyActionValue(
            valueToAssign.update,
            valueToAssign.value,
        );
        const nextBaseRow = buildBaseActionRow(
            valueToAssign,
            defaultConditions,
        );

        setRows((prev) => {
            let didUpdate = false;
            const nextRows = prev.map((row) => {
                const nextRow = syncBaseRowWithDefaults({
                    row,
                    valueToAssign,
                    nextBaseActionKey,
                    nextBaseRow,
                });
                didUpdate = didUpdate || nextRow !== row;
                return nextRow;
            });
            return didUpdate ? nextRows : prev;
        });
    }, [valueToAssign, defaultConditions]);

    // Propagate default group id the moment it becomes available asynchronously
    useEffect(() => {
        if (defaultRuleGroupId && !ruleGroupId) {
            setRuleGroupId(defaultRuleGroupId);
        }
    }, [defaultRuleGroupId, ruleGroupId]);

    /**
     * Derive each non-base row's owned transaction ids from `newValuesMap`.
     * Selection is intentionally not used here — selection is a UX gesture
     * that scopes the "Update [field]" button, not action ownership.
     *
     * Entries whose value matches the base row's value are skipped: they are
     * implicitly owned by the base row, whose count is computed as
     * `totalTransactions - Σ(non-base owned ids)`.
     */
    const rowIdToOwnedTxIds = useMemo(
        () =>
            buildRowIdToOwnedTxIds({
                rows,
                newValuesMap,
                valueToAssign,
            }),
        [rows, newValuesMap, valueToAssign],
    );

    /**
     * Reconciliation pass: keeps the row list in sync with `newValuesMap`.
     *
     *   1. For every non-base value appearing in `newValuesMap` that no
     *      existing row represents, prepend a fresh `auto`-source row.
     *   2. Drop any `auto` row whose value is no longer present anywhere in
     *      `newValuesMap`. `base` rows always persist (they represent every
     *      transaction not covered by an override). `manual` rows (user-
     *      added via "Add action") always persist — the user is authoring a
     *      rule definition, so its bucket is incidental.
     *
     * Row edits are not reconciled — they don't change `newValuesMap`, so
     * there is nothing to reconcile against. Only external `newValuesMap`
     * changes are reconciled, by the effect below through a functional state
     * update.
     */
    const reconcileRows = useCallback(
        (nextRows: ActionRowDefinition[]) =>
            reconcileActionRowsWithNewValues({
                rows: nextRows,
                newValuesMap,
                valueToAssign,
            }),
        [newValuesMap, valueToAssign],
    );

    useEffect(() => {
        setRows((prev) => reconcileRows(prev));
    }, [reconcileRows]);

    const updateRow = useCallback<ActionsContextValue["updateRow"]>(
        (id, patch) => {
            setRows((prev) =>
                prev.map((r) => {
                    if (r.id !== id) {
                        return r;
                    }
                    const next: ActionRowDefinition = { ...r };
                    if (patch.conditions !== undefined) {
                        next.conditions = patch.conditions;
                    }
                    if (patch.action !== undefined) {
                        next.action = patch.action;
                        if (patch.action.actionType) {
                            next.actionType = patch.action.actionType;
                        }
                    }
                    if (patch.createRule !== undefined) {
                        next.createRule = patch.createRule;
                    }
                    if (patch.ruleMatch !== undefined) {
                        next.ruleMatch = patch.ruleMatch ?? undefined;
                    }
                    if (patch.defaultConditionIds !== undefined) {
                        next.defaultConditionIds = patch.defaultConditionIds;
                    }
                    return next;
                }),
            );
        },
        [],
    );

    const upsertFilterScopedRow = useCallback<
        ActionsContextValue["upsertFilterScopedRow"]
    >(
        ({ value, filters: scopedFilters, transactionCount }) => {
            const nextRow = buildFilterScopedActionRow({
                valueToAssign,
                overrideValue: value,
                filters: scopedFilters,
                transactionCount,
            });

            setRows((prev) => {
                const existingIndex = prev.findIndex(
                    (row) =>
                        row.filterScope &&
                        isEqual(row.filterScope.filters, scopedFilters),
                );
                if (existingIndex === -1) {
                    return [nextRow, ...prev];
                }

                const existing = prev[existingIndex];
                const next = [...prev];
                next[existingIndex] = {
                    ...nextRow,
                    id: existing.id,
                    createRule: existing.createRule,
                    ruleMatch: undefined,
                    defaultConditionIds: existing.defaultConditionIds,
                };
                return next;
            });
        },
        [valueToAssign],
    );

    const appendEmptyRow = useCallback(() => {
        const newRow = buildManualActionRow({
            valueToAssign,
            defaultConditions,
        });
        setRows((prev) => [newRow, ...prev]);
    }, [valueToAssign, defaultConditions]);

    const removeRow = useCallback((id: string) => {
        setRows((prev) => removeActionRow(prev, id));
    }, []);

    const reorderRows = useCallback((fromIndex: number, toIndex: number) => {
        setRows((prev) => {
            if (
                fromIndex < 0 ||
                fromIndex >= prev.length ||
                toIndex < 0 ||
                toIndex >= prev.length ||
                fromIndex === toIndex
            ) {
                return prev;
            }
            const reordered = [...prev];
            const [moved] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, moved);
            return reordered;
        });
    }, []);

    const isRuleAuthoringAvailable = useMemo(
        () =>
            defaultConditions.length > 0 ||
            rows.some((row) => row.conditions.length > 0),
        [defaultConditions, rows],
    );

    const value = useMemo<ActionsContextValue>(
        () => ({
            rows,
            ruleGroupId,
            setRuleGroupId,
            valueToAssign,
            filters,
            similarToFallbackLabel,
            defaultConditions,
            isRuleAuthoringAvailable,
            scopeConditions,
            matchingScopeConditions,
            totalTransactions,
            rowIdToOwnedTxIds,
            updateRow,
            upsertFilterScopedRow,
            appendEmptyRow,
            removeRow,
            reorderRows,
        }),
        [
            rows,
            ruleGroupId,
            valueToAssign,
            filters,
            similarToFallbackLabel,
            defaultConditions,
            isRuleAuthoringAvailable,
            scopeConditions,
            matchingScopeConditions,
            totalTransactions,
            rowIdToOwnedTxIds,
            updateRow,
            upsertFilterScopedRow,
            appendEmptyRow,
            removeRow,
            reorderRows,
        ],
    );

    return (
        <ActionsContext.Provider value={value}>
            {children}
        </ActionsContext.Provider>
    );
};
